import { useCallback, useEffect, useMemo, useState } from "react";
import { convexErrMessage } from "@/lib/convex-error";
import { useAuth } from "@/hooks/use-auth";
import { useDataClient } from "@/data/client";
import { useAuthState } from "@/data/user";
import { useCreateServerAttachmentMutation } from "@/data/servers";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { Button } from "@sphinx/design-system/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@sphinx/design-system/breadcrumb";
import { EvalsEmptyHero } from "./evals/evals-empty-hero";
import {
  runExcalidrawQuickstart,
  EXCALIDRAW_QUICKSTART_SUITE_NAME,
} from "@/lib/evals/excalidraw-quickstart";
import { EXCALIDRAW_SERVER_NAME } from "@/lib/excalidraw-quick-connect";
import { isQuickstartSuite } from "./evals/constants";
import type { ServerFormData } from "@/shared/types.js";
import { useProjectServers } from "@/hooks/useViews";
import { usePreviewedHostId } from "@/hooks/use-previewed-client-id";
import { useEvalsRouteFromUrl } from "@/lib/eval-route-url";
import { useEvalTabContext } from "@/hooks/use-eval-tab-context";
import { useEvalIterationQuota } from "@/hooks/use-eval-iteration-quota";
import { aggregateSuite, formatRunId } from "./evals/helpers";
import { EvalTabGate } from "./evals/EvalTabGate";
import { EvalsHeader } from "./evals/evals-header";
import {
  createPlaygroundSuiteNavigation,
  navigatePlaygroundEvalsRoute,
} from "./evals/create-suite-navigation";
import { SuiteIterationsView } from "./evals/suite-iterations-view";
import { SphinxRunDetail } from "./evals/sphinx-run-detail";
import { EvalRunner } from "./evals/eval-runner";
import { useQuickRun } from "./evals/use-quick-run";
import { useHostList } from "@/hooks/useClients";
import { ConfirmationDialogs } from "./evals/ConfirmationDialogs";
import { useEvalQueries } from "./evals/use-eval-queries";
import { useEvalMutations } from "./evals/use-eval-mutations";
import { useEvalHandlers } from "./evals/use-eval-handlers";
import { isDraftTestCaseId } from "./evals/draft-test-case";
import { SuiteSwitcher } from "./evals/suite-switcher";
import {
  sortSuiteOverviewEntries,
  stripTimestampSuffix,
} from "./evals/suite-overview-presentation";
import {
  CreateSuiteDialog,
  type CreateSuitePayload,
} from "./evals/create-suite-dialog";
import { track } from "@/lib/analytics";
import type { EvalChatHandoff } from "@/lib/eval-chat-handoff";
import type { EnsureServersReadyResult } from "@/hooks/use-app-state";

interface EvalsTabProps {
  projectId?: string | null;
  onContinueInChat?: (handoff: Omit<EvalChatHandoff, "id">) => void;
  ensureServersReady?: (
    serverNames: string[]
  ) => Promise<EnsureServersReadyResult>;
  handleConnect?: (config: ServerFormData) => void;
}

export function EvalsTab({
  projectId,
  onContinueInChat,
  ensureServersReady,
  handleConnect,
}: EvalsTabProps) {
  const { isAuthenticated } = useAuthState();

  return (
    <ErrorBoundary
      key={`${projectId ?? "none"}:${isAuthenticated ? "authed" : "guest"}`}
      fallback={({ error, reset }) => (
        <EvalTabErrorFallback error={error} onRetry={reset} />
      )}
    >
      <EvalsTabContent
        projectId={projectId}
        onContinueInChat={onContinueInChat}
        ensureServersReady={ensureServersReady}
        handleConnect={handleConnect}
      />
    </ErrorBoundary>
  );
}

function EvalTabErrorFallback({
  error,
  onRetry,
}: {
  error: Error | null;
  onRetry: () => void;
}) {
  return (
    <div className="p-6">
      <EmptyState
        icon={FlaskConical}
        title="Could not load Testing"
        description="Something went wrong while loading suites. Try again in a moment."
        className="h-[calc(100vh-200px)]"
      >
        {/* The actual crash, not just the apology — this screen kept eating
            the one piece of information that would let anyone fix it. */}
        {error?.message ? (
          <p className="max-w-lg break-words font-mono text-xs text-muted-foreground">
            {error.message}
          </p>
        ) : null}
        <Button type="button" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      </EmptyState>
    </div>
  );
}

function EvalsTabContent({
  projectId,
  onContinueInChat,
  ensureServersReady,
  handleConnect,
}: EvalsTabProps) {
  const { isAuthenticated, isLoading } = useAuthState();
  const { user } = useAuth();
  // create-suite-dialog uses `hostsEnabled` as both a feature gate AND a
  // "skeleton suite creation requires attachments" gate (attachmentsRequired
  // = hostsEnabled && projectId), so it stays auth-gated rather than
  // unconditionally on.
  const hostsEnabled = isAuthenticated;
  const route = useEvalsRouteFromUrl();
  // sphinx has a single always-authenticated operator; there are no guests.
  const isDirectGuest = false;
  const [previewedHostId] = usePreviewedHostId(projectId ?? null);
  const {
    organizationId,
    connectedServerNames,
    userMap,
    canDeleteArtifact,
    canDeleteRuns,
    availableModels,
  } = useEvalTabContext({
    isAuthenticated,
    projectId: projectId ?? null,
    isDirectGuest,
  });
  const { quota: evalIterationQuota } = useEvalIterationQuota({
    organizationId,
    enabled: Boolean(organizationId),
  });
  // No plan quotas in sphinx: the stubbed hook never reports a limit.
  const evalRunsDisabledReason: string | null = evalIterationQuota?.allowed !=
    null && evalIterationQuota.used >= evalIterationQuota.allowed
    ? "Eval iteration limit reached."
    : null;
  const { servers: projectServers = [] } = useProjectServers({
    isAuthenticated,
    projectId: projectId ?? null,
  });
  const mutations = useEvalMutations({ isDirectGuest });
  const convex = useDataClient();
  const createServerAttachmentMutation =
    useCreateServerAttachmentMutation() as unknown as (args: {
    projectId: string;
    name: string;
    serverIds: string[];
  }) => Promise<{ _id: string }>;

  const selectedSuiteId =
    route.type === "suite-overview" ||
    route.type === "run-detail" ||
    route.type === "test-detail" ||
    route.type === "test-edit" ||
    route.type === "suite-edit"
      ? route.suiteId
      : null;
  const selectedTestId =
    route.type === "test-detail" || route.type === "test-edit"
      ? route.testId
      : null;

  const overviewQueries = useEvalQueries({
    isAuthenticated: isAuthenticated && Boolean(projectId),
    selectedSuiteId: null,
    deletingSuiteId: null,
    projectId: projectId ?? null,
    organizationId: null,
    isDirectGuest,
  });

  // All suites are visible in Evaluate regardless of origin (ui or sdk/CI).
  // SDK-created suites get a CI badge in the switcher instead of being hidden.
  const visibleSuites = overviewQueries.sortedSuites;

  const selectedSuiteEntry = useMemo(() => {
    if (!selectedSuiteId) {
      return null;
    }
    return (
      visibleSuites.find((entry) => entry.suite._id === selectedSuiteId) ?? null
    );
  }, [selectedSuiteId, visibleSuites]);

  const latestRunBySuiteId = useMemo(
    () =>
      new Map(
        visibleSuites.map((entry) => [entry.suite._id, entry.latestRun ?? null])
      ),
    [visibleSuites]
  );

  const handlers = useEvalHandlers({
    mutations,
    selectedSuiteEntry,
    selectedSuiteId,
    selectedTestId,
    projectId: projectId ?? null,
    organizationId,
    connectedServerNames,
    ensureServersReady,
    latestRunBySuiteId,
    projectServers,
    availableModels,
  });
  const {
    deletingSuiteId,
    rerunningSuiteId,
    cancellingRunId,
    deletingRunId,
    directDeleteTestCase,
  } = handlers;

  const guardEvalIterationQuota = useCallback(() => {
    if (!evalRunsDisabledReason) {
      return true;
    }
    toast.error(evalRunsDisabledReason);
    return false;
  }, [evalRunsDisabledReason]);

  // Suite "Run" → the sphinx run wizard (targets + yarn env), which POSTs
  // /api/sphinx/eval-suites/:id/runs. `runWizardCaseIds` narrows the wizard to
  // a single case (quick run with no enabled target); null = whole suite.
  const [runWizardOpen, setRunWizardOpen] = useState(false);
  const [runWizardCaseIds, setRunWizardCaseIds] = useState<string[] | null>(null);
  const handleOpenRunWizard = useCallback(() => {
    if (!guardEvalIterationQuota()) return;
    setRunWizardCaseIds(null);
    setRunWizardOpen(true);
  }, [guardEvalIterationQuota]);
  const handleRunWizardOpenChange = useCallback((open: boolean) => {
    setRunWizardOpen(open);
    if (!open) setRunWizardCaseIds(null);
  }, []);

  // Per-case play button → a sphinx run for that case on the enabled targets.
  const { hosts: targets } = useHostList({ isAuthenticated, projectId: projectId ?? null });
  const quickRun = useQuickRun({
    suiteId: selectedSuiteId ?? null,
    targets,
    onNeedTargets: (caseId) => {
      setRunWizardCaseIds([caseId]);
      setRunWizardOpen(true);
    },
  });
  const handleQuickRunTestCase = useCallback(
    (caseId: string) => {
      if (!guardEvalIterationQuota()) return;
      void quickRun.runCase(caseId);
    },
    [guardEvalIterationQuota, quickRun]
  );

  const queries = useEvalQueries({
    isAuthenticated: isAuthenticated && Boolean(projectId),
    selectedSuiteId,
    deletingSuiteId,
    projectId: projectId ?? null,
    organizationId: null,
    isDirectGuest,
  });

  const selectedSuite = queries.selectedSuite;
  const suiteDetails = queries.suiteDetails;
  const activeIterations = queries.activeIterations;
  const sortedIterations = queries.sortedIterations;
  const runsForSelectedSuite = queries.runsForSelectedSuite;

  const suiteAggregate = useMemo(() => {
    if (!selectedSuite || !suiteDetails) return null;
    return aggregateSuite(
      selectedSuite,
      suiteDetails.testCases,
      activeIterations
    );
  }, [selectedSuite, suiteDetails, activeIterations]);
  const playgroundNavigation = useMemo(
    () => createPlaygroundSuiteNavigation(),
    []
  );

  useEffect(() => {
    if (route.type === "list" || route.type === "create") {
      return;
    }
    if (!selectedSuiteId) {
      return;
    }
    if (overviewQueries.isOverviewLoading) {
      return;
    }
    // System suites (the Playground's hidden dispatch suite) are absent from
    // the overview but resolve by id — don't bounce their deep links.
    if (queries.isSelectedSuiteResolving || queries.selectedSuite) {
      return;
    }
    if (!selectedSuiteEntry) {
      navigatePlaygroundEvalsRoute({ type: "list" }, { replace: true });
    }
  }, [
    overviewQueries.isOverviewLoading,
    queries.isSelectedSuiteResolving,
    queries.selectedSuite,
    route.type,
    selectedSuiteEntry,
    selectedSuiteId,
  ]);

  // No standalone suites list: landing on /evals jumps straight into the most
  // recently RUN suite's dashboard (suites are switched via the breadcrumb
  // dropdown). Never-run suites all tie, so a project with no runs falls back
  // to the sortedSuites recency order. Only the empty-state (no suites) keeps
  // the bare list route.
  useEffect(() => {
    if (route.type !== "list") {
      return;
    }
    if (overviewQueries.isOverviewLoading) {
      return;
    }
    const mostRecent = sortSuiteOverviewEntries(visibleSuites, "recently_run")[0];
    if (mostRecent) {
      navigatePlaygroundEvalsRoute(
        { type: "suite-overview", suiteId: mostRecent.suite._id },
        { replace: true }
      );
    }
  }, [route.type, overviewQueries.isOverviewLoading, visibleSuites]);

  // Wait for auth to settle before firing view events. The parent
  // ErrorBoundary keys on (projectId, isAuthenticated), so projectId
  // resolving null→"x" remounts this component and would otherwise
  // double-fire (once on the null mount, once on the resolved mount).
  useEffect(() => {
    if (isLoading) return;
    track("evaluate_tab_viewed", {
      location: "evals_tab",
      project_id: projectId ?? null,
    });
  }, [isLoading, projectId]);

  useEffect(() => {
    if (isLoading) return;
    if (!selectedSuiteId) return;
    track("suite_viewed", {
      location: "evals_tab",
      project_id: projectId ?? null,
      suite_id: selectedSuiteId,
      route_type: route.type,
    });
  }, [isLoading, selectedSuiteId, route.type, projectId]);

  // Name-only prefill for the create-suite dialog, set by the agent's
  // openEvalSuiteForm command (prefill-over-commit: the agent may suggest a
  // name; the user picks everything else and submits).
  const [createSuitePrefillName, setCreateSuitePrefillName] = useState<
    string | null
  >(null);

  const handleOpenCreateSuite = useCallback(() => {
    setCreateSuitePrefillName(null);
    navigatePlaygroundEvalsRoute({ type: "create" });
  }, []);

  const [isQuickstartRunning, setIsQuickstartRunning] = useState(false);

  const existingQuickstartSuiteId = useMemo(() => {
    const match = visibleSuites.find(
      (entry) =>
        isQuickstartSuite(entry.suite) ||
        entry.suite.name === EXCALIDRAW_QUICKSTART_SUITE_NAME
    );
    return match?.suite._id ?? null;
  }, [visibleSuites]);

  const handleExcalidrawQuickstart = useCallback(async () => {
    if (!handleConnect || isQuickstartRunning) return;
    if (!projectId) {
      toast.error("Select or create a project before running the quickstart.");
      return;
    }
    setIsQuickstartRunning(true);
    try {
      await runExcalidrawQuickstart({
        projectId,
        convex,
        createTestSuite: mutations.createTestSuiteMutation,
        createTestCase: mutations.createTestCaseMutation,
        createServerAttachment: createServerAttachmentMutation,
        handleConnect,
        isExcalidrawConnected: connectedServerNames.has(EXCALIDRAW_SERVER_NAME),
        existingQuickstartSuiteId,
        previewedHostId,
      });
    } finally {
      setIsQuickstartRunning(false);
    }
  }, [
    projectId,
    convex,
    handleConnect,
    isQuickstartRunning,
    mutations.createTestSuiteMutation,
    mutations.createTestCaseMutation,
    createServerAttachmentMutation,
    connectedServerNames,
    existingQuickstartSuiteId,
    previewedHostId,
  ]);

  const showQuickstart = Boolean(handleConnect);

  const handleCreateDialogChange = useCallback((open: boolean) => {
    if (!open) {
      setCreateSuitePrefillName(null);
      navigatePlaygroundEvalsRoute({ type: "list" }, { replace: true });
    }
  }, []);

  const handleCreateSuite = useCallback(
    async (payload: CreateSuitePayload) => {
      if (!projectId) {
        return;
      }

      try {
        const createdSuite = await mutations.createTestSuiteMutation({
          projectId,
          name: payload.name,
          // environment.servers is left empty: hosts own server selection
          // now, and the runner derives the per-run server set from each
          // attachment's snapshot. Suites with zero attachments are valid
          // skeletons — they just can't run until a host is attached.
          environment: { servers: [] },
          ...(payload.hostAttachments && payload.hostAttachments.length > 0
            ? { hostAttachments: payload.hostAttachments }
            : {}),
          ...(payload.serverAttachmentId
            ? { serverAttachmentId: payload.serverAttachmentId }
            : {}),
        });

        if (!createdSuite?._id) {
          throw new Error("Suite was created without an id");
        }

        toast.success("Suite created");
        navigatePlaygroundEvalsRoute({
          type: "suite-overview",
          suiteId: createdSuite._id,
        });
      } catch (error) {
        toast.error(convexErrMessage(error, "Failed to create suite"));
        throw error;
      }
    },
    [mutations.createTestSuiteMutation, projectId]
  );

  const handleSelectSuite = useCallback((suiteId: string) => {
    navigatePlaygroundEvalsRoute({ type: "suite-overview", suiteId });
  }, []);

  const handleNavigateToSuiteOverview = useCallback(() => {
    if (!selectedSuiteId) return;
    navigatePlaygroundEvalsRoute({
      type: "suite-overview",
      suiteId: selectedSuiteId,
    });
  }, [selectedSuiteId]);

  const isSuiteOverviewRoute = route.type === "suite-overview";

  const handleDeleteTestCasesBatch = useCallback(
    async (testCaseIds: string[]) => {
      const settledDeletes = await Promise.allSettled(
        testCaseIds.map(async (id) => {
          await directDeleteTestCase(id);
          return id;
        })
      );
      const deletedIds = new Set(
        settledDeletes.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : []
        )
      );
      const failedDeletes = settledDeletes.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );

      if (failedDeletes.length > 0) {
        console.error("Failed to delete some test cases:", failedDeletes);
        toast.error(
          `Failed to delete ${failedDeletes.length} test case${
            failedDeletes.length === 1 ? "" : "s"
          }.`
        );
      }

      if (selectedSuiteId && selectedTestId && deletedIds.has(selectedTestId)) {
        navigatePlaygroundEvalsRoute(
          {
            type: "suite-overview",
            suiteId: selectedSuiteId,
            view: "test-cases",
          },
          { replace: true }
        );
      }
    },
    [directDeleteTestCase, selectedSuiteId, selectedTestId]
  );

  const hasDetailRoute =
    selectedSuiteId &&
    (route.type === "suite-overview" ||
      route.type === "run-detail" ||
      route.type === "test-detail" ||
      route.type === "test-edit" ||
      route.type === "suite-edit");

  const selectedTestCase = useMemo(() => {
    if (!selectedTestId) return null;
    return (
      suiteDetails?.testCases.find((tc) => tc._id === selectedTestId) ?? null
    );
  }, [selectedTestId, suiteDetails]);

  const renderPlaygroundBreadcrumb = () => {
    if (!hasDetailRoute || !selectedSuite) return null;
    const selectedSuiteName =
      stripTimestampSuffix(selectedSuite.name || "") || "Untitled suite";

    return (
      <Breadcrumb className="min-w-0 flex-1">
        <BreadcrumbList className="min-w-0 flex-nowrap">
          <BreadcrumbItem>
            <SuiteSwitcher
              suites={visibleSuites}
              currentSuiteId={selectedSuite._id}
              onSelectSuite={handleSelectSuite}
              onCreateSuite={handleOpenCreateSuite}
              onDeleteSuite={handlers.handleDelete}
              canDeleteSuite={(suite) => canDeleteArtifact(suite.createdBy)}
            />
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem className="max-w-[min(220px,32vw)] min-w-0 sm:max-w-[280px]">
            {isSuiteOverviewRoute ? (
              <BreadcrumbPage
                className="truncate font-medium"
                title={selectedSuiteName}
              >
                {selectedSuiteName}
              </BreadcrumbPage>
            ) : (
              <BreadcrumbLink asChild>
                <button
                  type="button"
                  onClick={handleNavigateToSuiteOverview}
                  title={selectedSuiteName}
                  className="inline-flex max-w-full border-0 bg-transparent p-0 font-medium truncate"
                >
                  {selectedSuiteName}
                </button>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {route.type === "run-detail" ? (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="truncate font-medium">
                  Run {formatRunId(route.runId)}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : null}
          {route.type === "test-detail" || route.type === "test-edit" ? (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="max-w-[min(220px,32vw)] min-w-0">
                <BreadcrumbPage
                  className="truncate font-medium"
                  title={
                    selectedTestCase?.title ??
                    (isDraftTestCaseId(selectedTestId) ? "New case" : "Case")
                  }
                >
                  {selectedTestCase?.title ??
                    (isDraftTestCaseId(selectedTestId) ? "New case" : "Case")}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : null}
          {route.type === "suite-edit" ? (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="truncate font-medium">
                  Settings
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : null}
        </BreadcrumbList>
      </Breadcrumb>
    );
  };

  const renderSuitesBrowsePanel = () => {
    if (overviewQueries.isOverviewLoading) {
      return (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-sm text-muted-foreground">
              Loading suites...
            </p>
          </div>
        </div>
      );
    }

    if (visibleSuites.length === 0) {
      return (
        <EvalsEmptyHero
          onCreateSuite={handleOpenCreateSuite}
          onQuickstart={() => void handleExcalidrawQuickstart()}
          isQuickstartRunning={isQuickstartRunning}
          showQuickstart={showQuickstart}
        />
      );
    }

    if (hasDetailRoute) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          {queries.isSuiteDetailsLoading ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <div className="text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                <p className="mt-4 text-sm text-muted-foreground">
                  Loading suite data...
                </p>
              </div>
            </div>
          ) : (
            renderSuiteIterationsDetail()
          )}
        </div>
      );
    }

    // List route with suites: the redirect effect above is about to send us
    // into the most recent suite's dashboard. Show a spinner instead of the
    // (now removed) standalone list so there's no flash of an empty table.
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  };

  const renderSuiteIterationsDetail = () => {
    if (!selectedSuite) {
      return null;
    }

    if (route.type === "run-detail") {
      return (
        <SphinxRunDetail
          key={route.runId}
          runId={route.runId}
          cases={suiteDetails?.testCases ?? []}
          onBack={handleNavigateToSuiteOverview}
        />
      );
    }

    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-6">
        <SuiteIterationsView
          isDirectGuest={isDirectGuest}
          ensureServersReady={ensureServersReady}
          suite={selectedSuite}
          cases={suiteDetails?.testCases ?? []}
          iterations={activeIterations}
          allIterations={sortedIterations}
          runs={runsForSelectedSuite}
          runsLoading={queries.isSuiteRunsLoading}
          aggregate={suiteAggregate}
          alwaysShowEditIterationRows
          onEditTestCase={(testCaseId) =>
            playgroundNavigation.toTestEdit(selectedSuite._id, testCaseId, {
              openCompare: true,
            })
          }
          onCreateTestCase={async () =>
            handlers.handleCreateTestCase(selectedSuite._id)
          }
          onRerun={handleOpenRunWizard}
          onCancelRun={handlers.handleCancelRun}
          onDelete={handlers.handleDelete}
          onDeleteRun={handlers.handleDeleteRun}
          onDirectDeleteRun={handlers.directDeleteRun}
          connectedServerNames={connectedServerNames}
          canDeleteSuite={canDeleteArtifact(selectedSuite.createdBy)}
          rerunningSuiteId={rerunningSuiteId}
          cancellingRunId={cancellingRunId}
          deletingSuiteId={deletingSuiteId}
          deletingRunId={deletingRunId}
          availableModels={availableModels}
          route={route}
          userMap={userMap}
          projectId={projectId}
          navigation={playgroundNavigation}
          onContinueInChat={onContinueInChat}
          canDeleteRuns={canDeleteRuns}
          canDeleteRun={(run) => canDeleteArtifact(run.createdBy)}
          hideRunActions
          evalRunsDisabledReason={evalRunsDisabledReason}
          onDeleteTestCasesBatch={handleDeleteTestCasesBatch}
          onRunTestCase={(testCase) => handleQuickRunTestCase(testCase._id)}
          runningTestCaseId={quickRun.runningCaseId}
          projectServers={projectServers}
        />
      </div>
    );
  };

  const renderPlaygroundBody = () => renderSuitesBrowsePanel();

  return (
    <EvalTabGate
      variant="playground"
      isLoading={isLoading}
      isAuthenticated={isAuthenticated}
      user={user}
      projectId={projectId}
      isDirectGuest={isDirectGuest}
      header={
        <EvalsHeader>{renderPlaygroundBreadcrumb()}</EvalsHeader>
      }
    >
      <>
        <CreateSuiteDialog
          open={route.type === "create"}
          onOpenChange={handleCreateDialogChange}
          onSubmit={handleCreateSuite}
          hostsEnabled={hostsEnabled}
          projectId={projectId}
          initialName={createSuitePrefillName}
        />

        {selectedSuite ? (
          <EvalRunner
            projectId={projectId ?? ""}
            suite={{
              id: selectedSuite._id,
              name: selectedSuite.name,
              cases: suiteDetails?.testCases ?? [],
              caseIds: runWizardCaseIds ?? undefined,
            }}
            open={runWizardOpen}
            onOpenChange={handleRunWizardOpenChange}
          />
        ) : null}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {renderPlaygroundBody()}

          <ConfirmationDialogs
            suiteToDelete={handlers.suiteToDelete}
            setSuiteToDelete={handlers.setSuiteToDelete}
            deletingSuiteId={handlers.deletingSuiteId}
            onConfirmDeleteSuite={handlers.confirmDelete}
            runToDelete={handlers.runToDelete}
            setRunToDelete={handlers.setRunToDelete}
            deletingRunId={handlers.deletingRunId}
            onConfirmDeleteRun={handlers.confirmDeleteRun}
            testCaseToDelete={handlers.testCaseToDelete}
            setTestCaseToDelete={handlers.setTestCaseToDelete}
            deletingTestCaseId={handlers.deletingTestCaseId}
            onConfirmDeleteTestCase={handlers.confirmDeleteTestCase}
          />
        </div>
      </>
    </EvalTabGate>
  );
}
